import React, { useState } from "react";
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Popconfirm,
  message,
  Space,
  Divider,
  Select,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  useColorCodesQuery,
  useCreateColorCodeMutation,
  useUpdateColorCodeMutation,
  useDeleteColorCodeMutation,
} from "../../Services";
import { ColorCode } from "../../Types";
import { modalFooter, modalTitle } from "../AppUi";

interface ColorCodeModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (newColorCode?: ColorCode) => void;
}

export const ColorCodeModal: React.FC<ColorCodeModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: colorCodes = [], isLoading } = useColorCodesQuery({
    enabled: open,
  });

  const createMutation = useCreateColorCodeMutation();
  const updateMutation = useUpdateColorCodeMutation();
  const deleteMutation = useDeleteColorCodeMutation();

  const handleStartEdit = (record: ColorCode) => {
    setEditingId(record.id);
    form.setFieldsValue({
      name: record.name,
      description: record.description || "",
      status: record.status || "ACTIVE",
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    form.resetFields();
  };

  const handleFormSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        updateMutation.mutate(
          {
            id: editingId,
            name: values.name.trim(),
            description: values.description ? values.description.trim() : "",
            status: values.status || "ACTIVE",
          },
          {
            onSuccess: (res: any) => {
              if (res?.success) {
                message.success("Color Code updated successfully!");
                handleCancelEdit();
              } else {
                message.error(res?.message || "Failed to update Color Code.");
              }
            },
            onError: () => {
              message.error("Something went wrong.");
            },
          },
        );
      } else {
        createMutation.mutate(values, {
          onSuccess: (res: any) => {
            if (res?.success) {
              message.success("Color Code added successfully!");
              form.resetFields();

              if (onSuccess) {
                onSuccess(res?.data);
              }
            } else {
              message.error(res?.message || "Failed to add Color Code.");
            }
          },
          onError: () => {
            message.error("Something went wrong.");
          },
        });
      }
    } catch {
      message.error("Please enter a valid Color Code.");
    }
  };

  const handleDelete = async (id: string) => {
    deleteMutation.mutate(id, {
      onSuccess: (res: any) => {
        if (res?.success) {
          message.success("Color Code deleted successfully!");
          if (editingId === id) {
            handleCancelEdit();
          }
        } else {
          message.error(res?.message || "Failed to delete Color Code.");
        }
      },
      onError: () => {
        message.error("Something went wrong.");
      },
    });
  };

  const handleModalClose = () => {
    handleCancelEdit();
    onClose();
  };

  const columns = [
    {
      title: "Color Code",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (text: string, record: ColorCode) => (
        <span
          className={`font-mono font-semibold ${
            record.id === editingId
              ? "text-blue-700 font-bold"
              : "text-blue-700"
          }`}
        >
          {text}
        </span>
      ),
    },

    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string) => (
        <span className="text-gray-600 text-xs">{text || "-"}</span>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status: ColorCode["status"]) => (
        <span
          className={
            status === "ACTIVE"
              ? "text-green-600 font-medium"
              : "text-gray-400 font-medium"
          }
        >
          {status || "ACTIVE"}
        </span>
      ),
    },

    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center" as const,
      render: (_: any, record: ColorCode) => (
        <Space size="small">
          <Button
            type="text"
            icon={
              <EditOutlined
                className={record.id === editingId ? "text-blue-600" : ""}
              />
            }
            size="small"
            onClick={() => handleStartEdit(record)}
          />

          <Popconfirm
            title="Are you sure you want to delete this Color Code?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title={modalTitle("Manage Color Codes")}
      open={open}
      onCancel={handleModalClose}
      footer={modalFooter({
        actions: [
          {
            label: "Close",
            onClick: handleModalClose,
          },
        ],
      })}
      width={650}
    >
      <Form
        form={form}
        layout="vertical"
        className="my-3 bg-slate-50 border border-slate-200 p-3 rounded-lg"
      >
        <div className="grid grid-cols-3 gap-3">
          <Form.Item
            name="name"
            label="Color Code"
            rules={[
              {
                required: true,
                message: "Color Code is required",
              },
            ]}
            className="mb-0"
          >
            <Input placeholder="e.g. RAL 9016" prefix={<PlusOutlined />} />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description (Optional)"
            className="mb-0"
          >
            <Input placeholder="e.g. Traffic White" />
          </Form.Item>

          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
            className="mb-0"
          >
            <Select
              options={[
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
              ]}
            />
          </Form.Item>
        </div>

        <div className="flex justify-end gap-2 mt-3">
          {editingId && (
            <Button size="middle" onClick={handleCancelEdit}>
              Cancel
            </Button>
          )}
          <Button
            type="primary"
            size="middle"
            onClick={handleFormSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            className={
              editingId
                ? "bg-blue-600 hover:bg-blue-700 font-semibold"
                : "bg-emerald-600 hover:bg-emerald-700 font-semibold"
            }
          >
            {editingId ? "Update Color Code" : "Add Color Code"}
          </Button>
        </div>
      </Form>

      <Divider style={{ margin: "12px 0" }} />

      <Table
        dataSource={colorCodes}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{
          pageSize: 5,
          size: "small",
          showSizeChanger: false,
        }}
        bordered
      />
    </Modal>
  );
};
